/**
 * File utility functions for smart filename generation and file operations
 */

import { Artefact, ArtefactType } from "@/types/artefacts";

import { generateDocumentMetadata } from "./documentUtils";
import { parseTableContent } from "./tableUtils";

/**
 * Sanitize filename by removing invalid characters
 */
export function sanitizeFilename(filename: string): string {
  return filename
    .toLowerCase()
    .replace(/[^a-z0-9\-_.]/g, "_") // Replace invalid chars with underscore
    .replace(/_{2,}/g, "_") // Replace multiple underscores with single
    .replace(/^_+|_+$/g, "") // Remove leading/trailing underscores
    .substring(0, 50); // Limit length
}

/**
 * Generate content-based filename for tables
 */
export function generateTableFilename(artefact: Artefact): string {
  const tableData = parseTableContent(artefact.content);

  if (!tableData) {
    return sanitizeFilename(artefact.title) || "table_data";
  }

  // Try to infer meaning from column headers
  const headers = tableData.headers.map((h) => h.toLowerCase());

  // Common data patterns
  if (headers.some((h) => h.includes("sales") || h.includes("revenue"))) {
    return "sales_data";
  }
  if (headers.some((h) => h.includes("user") || h.includes("customer"))) {
    return "user_data";
  }
  if (headers.some((h) => h.includes("product") || h.includes("item"))) {
    return "product_data";
  }
  if (headers.some((h) => h.includes("date") || h.includes("time"))) {
    return "time_series_data";
  }
  if (headers.some((h) => h.includes("price") || h.includes("cost"))) {
    return "financial_data";
  }
  if (headers.some((h) => h.includes("score") || h.includes("rating"))) {
    return "performance_data";
  }

  // Use first few column names as filename
  const keyColumns = headers.slice(0, 3).join("_");

  if (keyColumns) {
    return sanitizeFilename(keyColumns);
  }

  // Fallback to title or generic name
  return sanitizeFilename(artefact.title) || "table_data";
}

/**
 * Generate content-based filename for documents
 */
export function generateDocumentFilename(artefact: Artefact): string {
  const metadata = generateDocumentMetadata(artefact.content, artefact.title);

  // Use extracted title from content
  if (metadata.title && metadata.title !== "Untitled Document") {
    return sanitizeFilename(metadata.title);
  }

  // Try to infer document type from content
  const content = artefact.content.toLowerCase();

  if (content.includes("readme") || content.includes("getting started")) {
    return "readme";
  }
  if (content.includes("changelog") || content.includes("version history")) {
    return "changelog";
  }
  if (content.includes("license") || content.includes("copyright")) {
    return "license";
  }
  if (content.includes("api") || content.includes("endpoint")) {
    return "api_documentation";
  }
  if (content.includes("install") || content.includes("setup")) {
    return "installation_guide";
  }
  if (content.includes("tutorial") || content.includes("how to")) {
    return "tutorial";
  }
  if (content.includes("spec") || content.includes("requirement")) {
    return "specification";
  }

  // Fallback to sanitized title or generic name
  return sanitizeFilename(artefact.title) || "document";
}

/**
 * Generate content-based filename for code artefacts
 */
export function generateCodeFilename(artefact: Artefact): string {
  const content = artefact.content.toLowerCase();

  // Try to extract function/class names
  const functionMatch = content.match(/(?:function|def|const|let|var)\s+([a-zA-Z_][a-zA-Z0-9_]*)/);

  if (functionMatch) {
    return sanitizeFilename(functionMatch[1]);
  }

  const classMatch = content.match(/(?:class|interface)\s+([a-zA-Z_][a-zA-Z0-9_]*)/);

  if (classMatch) {
    return sanitizeFilename(classMatch[1]);
  }

  // Try to infer purpose from content
  if (content.includes("test") || content.includes("spec")) {
    return "test_code";
  }
  if (content.includes("util") || content.includes("helper")) {
    return "utilities";
  }
  if (content.includes("config") || content.includes("setting")) {
    return "configuration";
  }
  if (content.includes("api") || content.includes("endpoint")) {
    return "api_code";
  }
  if (content.includes("component") || content.includes("widget")) {
    return "component_code";
  }

  // Fallback to sanitized title or language-based name
  const baseName = sanitizeFilename(artefact.title) || artefact.language || "code";

  return baseName;
}

/**
 * Generate smart filename based on artefact type and content
 */
export function generateSmartFilename(artefact: Artefact): string {
  let baseName: string;

  switch (artefact.type) {
    case ArtefactType.TABLE:
      baseName = generateTableFilename(artefact);
      break;
    case ArtefactType.DOCUMENT:
      baseName = generateDocumentFilename(artefact);
      break;
    case ArtefactType.CODE:
      baseName = generateCodeFilename(artefact);
      break;
    default:
      baseName = sanitizeFilename(artefact.title) || "artefact";
  }

  // Ensure we have a valid filename
  if (!baseName || baseName === "_") {
    baseName = "untitled";
  }

  return baseName;
}

/**
 * Generate filename with timestamp for uniqueness
 */
export function generateFilenameWithTimestamp(baseName: string, extension: string): string {
  const timestamp = new Date().toISOString().split("T")[0]; // YYYY-MM-DD

  return `${baseName}_${timestamp}.${extension}`;
}

/**
 * Generate complete filename for download
 */
export function generateDownloadFilename(artefact: Artefact, extension: string): string {
  const baseName = generateSmartFilename(artefact);

  return generateFilenameWithTimestamp(baseName, extension);
}

/**
 * Get file extension based on artefact type
 */
export function getFileExtension(artefact: Artefact, format?: string): string {
  if (format) {
    switch (format) {
      case "csv":
        return "csv";
      case "json":
        return "json";
      case "markdown":
        return "md";
      case "html":
        return "html";
      default:
        break;
    }
  }

  switch (artefact.type) {
    case ArtefactType.TABLE:
      return "csv";
    case ArtefactType.DOCUMENT:
      return "md";
    case ArtefactType.CODE:
      return artefact.language || "txt";
    case ArtefactType.HTML:
      return "html";
    default:
      return "txt";
  }
}

/**
 * Generate MIME type based on file extension
 */
export function getMimeType(extension: string): string {
  const mimeTypes: Record<string, string> = {
    csv: "text/csv;charset=utf-8",
    json: "application/json;charset=utf-8",
    md: "text/markdown;charset=utf-8",
    txt: "text/plain;charset=utf-8",
    html: "text/html;charset=utf-8",
    js: "application/javascript;charset=utf-8",
    ts: "application/typescript;charset=utf-8",
    py: "text/x-python;charset=utf-8",
    css: "text/css;charset=utf-8",
    xml: "application/xml;charset=utf-8",
  };

  return mimeTypes[extension] || "text/plain;charset=utf-8";
}

/**
 * Document types supported by the application
 */
export type DocumentType = "pdf" | "docx" | "pptx" | "image" | "txt" | "csv" | "excel" | "unknown";

/**
 * Detect document type from filename extension
 * Centralised utility to ensure consistent document type detection across the application
 *
 * @param filename - The filename to analyse
 * @returns The detected document type
 */
export function detectDocumentType(filename: string): DocumentType {
  const lower = filename.toLowerCase();

  // PDF documents
  if (lower.endsWith(".pdf")) return "pdf";

  // Word documents
  if (lower.endsWith(".docx") || lower.endsWith(".doc")) return "docx";

  // PowerPoint documents
  if (lower.endsWith(".pptx") || lower.endsWith(".ppt")) return "pptx";

  // Plain text documents
  if (lower.endsWith(".txt")) return "txt";

  // CSV/TSV documents
  if (lower.endsWith(".csv") || lower.endsWith(".tsv")) return "csv";

  // Excel documents
  if (lower.endsWith(".xlsx") || lower.endsWith(".xls")) return "excel";

  // Image formats
  if (
    lower.endsWith(".png") ||
    lower.endsWith(".jpg") ||
    lower.endsWith(".jpeg") ||
    lower.endsWith(".gif") ||
    lower.endsWith(".webp") ||
    lower.endsWith(".bmp") ||
    lower.endsWith(".svg")
  ) {
    return "image";
  }

  return "unknown";
}

/**
 * Detect document type from MIME type and/or filename
 * Provides comprehensive detection by checking both MIME type and filename extension
 *
 * @param mimeType - Optional MIME type of the file
 * @param filename - Optional filename to analyse
 * @returns The detected document type, or null if cannot be determined
 */
export function detectDocumentTypeFromMimeAndFilename(
  mimeType?: string,
  filename?: string,
): DocumentType | null {
  const type = (mimeType || "").toLowerCase();
  const name = (filename || "").toLowerCase();

  // PDF documents
  if (type === "application/pdf" || name.endsWith(".pdf")) {
    return "pdf";
  }

  // Word documents
  if (
    type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
    type === "application/msword" ||
    name.endsWith(".docx") ||
    name.endsWith(".doc")
  ) {
    return "docx";
  }

  // Excel documents
  if (
    type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
    type === "application/vnd.ms-excel" ||
    name.endsWith(".xlsx") ||
    name.endsWith(".xls")
  ) {
    return "excel";
  }

  // PowerPoint documents
  if (
    type === "application/vnd.openxmlformats-officedocument.presentationml.presentation" ||
    type === "application/vnd.ms-powerpoint" ||
    name.endsWith(".pptx") ||
    name.endsWith(".ppt")
  ) {
    return "pptx";
  }

  // Image documents
  if (
    type.startsWith("image/") ||
    name.endsWith(".png") ||
    name.endsWith(".jpg") ||
    name.endsWith(".jpeg") ||
    name.endsWith(".gif") ||
    name.endsWith(".webp") ||
    name.endsWith(".bmp") ||
    name.endsWith(".svg")
  ) {
    return "image";
  }

  // Plain text documents
  if (
    type === "text/plain" ||
    type === "text/markdown" ||
    type === "text/html" ||
    name.endsWith(".txt") ||
    name.endsWith(".md") ||
    name.endsWith(".html")
  ) {
    return "txt";
  }

  // CSV documents
  if (type === "text/csv" || name.endsWith(".csv") || name.endsWith(".tsv")) {
    return "csv";
  }

  return null;
}

/**
 * Infer MIME type from filename, with optional fallback
 * More comprehensive than detectDocumentType, includes all file extensions and MIME types
 *
 * @param filename - The filename to analyse (optional)
 * @param fallback - Fallback MIME type if detection fails (optional)
 * @returns The detected or fallback MIME type
 */
export function inferMimeFromFilename(filename?: string, fallback?: string): string {
  if (!filename) return fallback || "application/octet-stream";
  const lower = filename.toLowerCase();

  // Documents
  if (lower.endsWith(".pdf")) return "application/pdf";
  if (lower.endsWith(".doc")) return "application/msword";
  if (lower.endsWith(".docx"))
    return "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
  if (lower.endsWith(".xls")) return "application/vnd.ms-excel";
  if (lower.endsWith(".xlsx"))
    return "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
  if (lower.endsWith(".ppt")) return "application/vnd.ms-powerpoint";
  if (lower.endsWith(".pptx"))
    return "application/vnd.openxmlformats-officedocument.presentationml.presentation";

  // Images
  if (lower.endsWith(".png")) return "image/png";
  if (lower.endsWith(".jpg") || lower.endsWith(".jpeg")) return "image/jpeg";
  if (lower.endsWith(".gif")) return "image/gif";
  if (lower.endsWith(".webp")) return "image/webp";
  if (lower.endsWith(".svg")) return "image/svg+xml";
  if (lower.endsWith(".bmp")) return "image/bmp";

  // Text formats
  if (lower.endsWith(".txt")) return "text/plain";
  if (lower.endsWith(".md")) return "text/markdown";
  if (lower.endsWith(".html") || lower.endsWith(".htm")) return "text/html";
  if (lower.endsWith(".csv")) return "text/csv";
  if (lower.endsWith(".tsv")) return "text/tab-separated-values";

  // Data formats
  if (lower.endsWith(".json")) return "application/json";
  if (lower.endsWith(".xml")) return "application/xml";

  // Fallback
  return fallback || "application/octet-stream";
}

/**
 * Result of file preview URL generation
 */
export interface FilePreviewUrlResult {
  success: boolean;
  blobUrl?: string;
  mimeType?: string;
  error?: string;
}

/**
 * Fetch and create a blob URL for file preview
 * Centralised utility to avoid duplicate URL construction and error handling
 *
 * @param temporaryUrl - Relative path from createTemporaryDownloadUrl
 * @param baseUrl - Base URL of the platform API
 * @param authenticatedFetch - Authenticated fetch function
 * @param fileName - Original filename for MIME type inference
 * @param signal - AbortSignal for cancellation
 * @returns FilePreviewUrlResult with blob URL or error
 */
export async function createFilePreviewUrl(
  temporaryUrl: string,
  baseUrl: string,
  authenticatedFetch: (url: string, init?: RequestInit) => Promise<Response>,
  fileName: string,
  signal?: AbortSignal,
): Promise<FilePreviewUrlResult> {
  try {
    // Construct absolute URL
    const apiBase = String(baseUrl).replace(/\/$/, "");
    const absoluteUrl = `${apiBase}${temporaryUrl.startsWith("/") ? "" : "/"}${temporaryUrl}`;

    // Fetch file with authentication
    const response = await authenticatedFetch(absoluteUrl, { signal });

    if (!response.ok) {
      return {
        success: false,
        error: `HTTP ${response.status}: ${response.statusText}`,
      };
    }

    // Get blob and infer MIME type
    const blob = await response.blob();
    const headerType = response.headers.get("Content-Type") || "";
    const inferredMime = inferMimeFromFilename(fileName, headerType || "application/octet-stream");

    // Override blob type if needed
    const needsOverride =
      !blob.type || blob.type === "application/octet-stream" || blob.type === "binary/octet-stream";

    const finalMime = needsOverride ? inferredMime : blob.type;
    const typedBlob = needsOverride ? new Blob([blob], { type: finalMime }) : blob;

    // Create object URL
    const blobUrl = URL.createObjectURL(typedBlob);

    return {
      success: true,
      blobUrl,
      mimeType: finalMime,
    };
  } catch (error: any) {
    // Handle abort separately
    if (error?.name === "AbortError") {
      return {
        success: false,
        error: "Request was cancelled",
      };
    }

    return {
      success: false,
      error: error?.message || "Failed to fetch file",
    };
  }
}
